import React, { useEffect, useState } from 'react';
import { formatDate, parseDateInputToIso } from './formatting';
import { DateInput } from './DateInput';

export type DateRange = { from: string | null; to: string | null };
export type DateRangePreset =
    | 'today' | 'yesterday' | '7d' | '30d'
    | 'this_month' | 'last_month' | 'this_quarter' | 'this_year'
    | 'all' | 'custom';

type Locale = 'hr' | 'en';

const PRESET_LABELS: Record<Exclude<DateRangePreset, 'custom'>, [string, string]> = {
    today: ['Danas', 'Today'],
    yesterday: ['Jučer', 'Yesterday'],
    '7d': ['7 dana', '7 days'],
    '30d': ['30 dana', '30 days'],
    this_month: ['Ovaj mjesec', 'This month'],
    last_month: ['Prošli mjesec', 'Last month'],
    this_quarter: ['Ovaj kvartal', 'This quarter'],
    this_year: ['Ova godina', 'This year'],
    all: ['Sve', 'All'],
};

function pad(n: number): string { return n < 10 ? '0' + n : String(n); }
function iso(d: Date): string { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }
function startOfDay(d: Date): Date { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
function addDays(d: Date, n: number): Date { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function startOfMonth(d: Date): Date { const x = new Date(d); x.setDate(1); x.setHours(0, 0, 0, 0); return x; }
function endOfMonth(d: Date): Date { const x = new Date(d); x.setMonth(x.getMonth() + 1); x.setDate(0); x.setHours(0, 0, 0, 0); return x; }

export function computeRangeForPreset(preset: Exclude<DateRangePreset, 'custom'>): DateRange {
    const today = startOfDay(new Date());
    switch (preset) {
        case 'today': return { from: iso(today), to: iso(today) };
        case 'yesterday': { const y = addDays(today, -1); return { from: iso(y), to: iso(y) }; }
        case '7d': return { from: iso(addDays(today, -6)), to: iso(today) };
        case '30d': return { from: iso(addDays(today, -29)), to: iso(today) };
        case 'this_month': return { from: iso(startOfMonth(today)), to: iso(today) };
        case 'last_month': {
            const firstThis = startOfMonth(today);
            const lastPrev = addDays(firstThis, -1);
            return { from: iso(startOfMonth(lastPrev)), to: iso(endOfMonth(lastPrev)) };
        }
        case 'this_quarter': {
            const qStartMonth = Math.floor(today.getMonth() / 3) * 3;
            const qStart = new Date(today.getFullYear(), qStartMonth, 1);
            return { from: iso(qStart), to: iso(today) };
        }
        case 'this_year': {
            const y = new Date(today.getFullYear(), 0, 1);
            return { from: iso(y), to: iso(today) };
        }
        case 'all': return { from: null, to: null };
    }
}

function rangesEqual(a: DateRange, b: DateRange): boolean {
    return (a.from ?? '') === (b.from ?? '') && (a.to ?? '') === (b.to ?? '');
}

function detectPreset(value: DateRange, presets: Array<Exclude<DateRangePreset, 'custom'>>): DateRangePreset {
    for (const p of presets) {
        if (rangesEqual(computeRangeForPreset(p), value)) return p;
    }
    return 'custom';
}

const BROWN = 'var(--ad-ink)';
const GOLD = 'var(--ad-acc)';

function HrDateField({ isoValue, onCommit, placeholder }: {
    isoValue: string | null;
    onCommit: (iso: string | null) => void;
    placeholder?: string;
}) {
    const [draft, setDraft] = useState(() => isoValue && /^\d{4}-\d{2}-\d{2}$/.test(isoValue) ? formatDate(isoValue, 'hr') : '');

    useEffect(() => {
        if (isoValue && /^\d{4}-\d{2}-\d{2}$/.test(isoValue)) setDraft(formatDate(isoValue, 'hr'));
        else setDraft('');
    }, [isoValue]);

    return (
        <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            placeholder={placeholder ?? 'dd.mm.gggg'}
            spellCheck={false}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => {
                const trimmed = draft.trim();
                if (trimmed === '') { onCommit(null); return; }
                const parsed = parseDateInputToIso(trimmed);
                if (parsed) onCommit(parsed);
                else if (isoValue) setDraft(formatDate(isoValue, 'hr')); // revert
                else setDraft('');
            }}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
            style={{
                width: 110, padding: '4px 8px', fontSize: '0.82rem',
                border: '1px solid rgba(var(--ad-soft-rgb),0.35)', borderRadius: 6, background: '#fffdf7',
                fontFamily: 'ui-monospace, monospace',
            }}
        />
    );
}

export function DateRangeFilter({
    value,
    onChange,
    locale,
    presets = ['today', 'yesterday', '7d', '30d', 'this_month', 'last_month', 'this_year', 'all'],
    allowCustom = true,
    label,
}: {
    value: DateRange;
    onChange: (r: DateRange) => void;
    locale: Locale;
    presets?: Array<Exclude<DateRangePreset, 'custom'>>;
    allowCustom?: boolean;
    label?: string;
}) {
    const active = detectPreset(value, presets);
    const t = (h: string, e: string) => locale === 'hr' ? h : e;

    return (
        <div
            style={{
                display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center',
                padding: 10, background: 'var(--ad-bg)', border: '1px solid rgba(var(--ad-soft-rgb),0.25)',
                borderRadius: 8, marginBottom: 12,
            }}
        >
            <span style={{ fontSize: '0.78rem', color: BROWN, fontWeight: 600, marginRight: 4 }}>
                📅 {label ?? t('Razdoblje', 'Period')}
            </span>
            {presets.map((p) => {
                const isActive = active === p;
                return (
                    <button
                        key={p}
                        type="button"
                        onClick={() => onChange(computeRangeForPreset(p))}
                        style={{
                            padding: '3px 10px',
                            fontSize: '0.78rem',
                            border: `1px solid ${isActive ? GOLD : 'rgba(var(--ad-soft-rgb),0.35)'}`,
                            background: isActive ? GOLD : '#fffdf7',
                            color: isActive ? '#fff' : BROWN,
                            borderRadius: 14,
                            cursor: 'pointer',
                            fontWeight: isActive ? 700 : 500,
                            transition: 'all 0.15s',
                        }}
                    >
                        {t(PRESET_LABELS[p][0], PRESET_LABELS[p][1])}
                    </button>
                );
            })}
            {allowCustom ? (
                <span style={{ display: 'flex', gap: 4, alignItems: 'center', marginLeft: 6 }}>
                    <span style={{
                        padding: '3px 8px', fontSize: '0.78rem', color: active === 'custom' ? '#fff' : BROWN,
                        background: active === 'custom' ? GOLD : 'transparent', borderRadius: 14,
                        fontWeight: active === 'custom' ? 700 : 500,
                    }}>{t('od', 'from')}:</span>
                    {locale === 'hr'
                        ? <HrDateField isoValue={value.from} onCommit={(iso) => onChange({ ...value, from: iso })} />
                        : <DateInput className="bi-input" value={value.from ?? ''} onChange={(iso) => onChange({ ...value, from: iso || null })} />}
                    <span style={{ fontSize: '0.78rem', color: BROWN }}>{t('do', 'to')}:</span>
                    {locale === 'hr'
                        ? <HrDateField isoValue={value.to} onCommit={(iso) => onChange({ ...value, to: iso })} />
                        : <DateInput className="bi-input" value={value.to ?? ''} onChange={(iso) => onChange({ ...value, to: iso || null })} />}
                </span>
            ) : null}
        </div>
    );
}
